import { useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { loadLobbyQueueAsync, pollLobbyQueueAsync } from '../redux/flows/portal/lobby'
import { useAppSelector, store } from '../redux/store'
import { APIStatus } from '../redux/types'
import getTodayInUserTimezone from '../utils/getTodayInUserTimezone'
import { getUserTimezone } from '../utils/GetUserTimezone'
import { normalizePollTimestampToToday } from '../utils/NormaliseTimeStampToToday'
import { utcMidnight } from '../utils/UtcMidnight'

const POLL_INTERVAL = 3000 // 3 seconds
const MAX_PENDING_TIME = 12000 // 12 seconds (longer than axios timeout)

export const useLobbyPoll = () => {
  const dispatch = useDispatch()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const pendingStartTimeRef = useRef<number | null>(null)
  const lastDispatchTimeRef = useRef<number>(0)
  const today = getTodayInUserTimezone()

  const clinicId = useAppSelector((state) => state.studioAuth.studioDetails?.id)
  const refreshDate = useAppSelector((state) => state.lobbyPortal.refreshDate)
  const pollStatus = useAppSelector((state) => state.lobbyPortal.pollLobbyQueueStatus)
  const lastPollTimestamp = useAppSelector((state) => state.lobbyPortal.lastPollTimestamp)

  // Track pending state
  useEffect(() => {
    if (pollStatus === APIStatus.pending && pendingStartTimeRef.current === null) {
      pendingStartTimeRef.current = Date.now()
    } else if (pollStatus !== APIStatus.pending) {
      pendingStartTimeRef.current = null
    }
  }, [pollStatus])

  const executePoll = useCallback(() => {
    if (!clinicId) return

    try {
      const now = Date.now()
      const timeSinceLastDispatch = now - lastDispatchTimeRef.current

      // Prevent dispatching too frequently (safety check)
      if (timeSinceLastDispatch < 1000) {
        return
      }

      const state = store.getState()
      const currentPollStatus = state.lobbyPortal.pollLobbyQueueStatus
      const currentLastPollTimestamp = state.lobbyPortal.lastPollTimestamp

      // Check if stuck in pending
      if (currentPollStatus === APIStatus.pending) {
        const pendingDuration = pendingStartTimeRef.current ? now - pendingStartTimeRef.current : 0

        if (pendingDuration > MAX_PENDING_TIME) {
          console.error(`[LobbyPoll] Request STUCK in pending for ${pendingDuration}ms - forcing recovery`)
          pendingStartTimeRef.current = null
          // Continue to dispatch new request
        } else {
          return
        }
      }

      // Calculate next timestamp
      let timestamp: string

      if (currentLastPollTimestamp) {
        const userTimezone = getUserTimezone()

        const lastDateInUserTZ = new Intl.DateTimeFormat('en-CA', {
          timeZone: userTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(currentLastPollTimestamp))

        const normalized =
          lastDateInUserTZ === today
            ? currentLastPollTimestamp
            : normalizePollTimestampToToday(currentLastPollTimestamp)

        if (currentPollStatus === APIStatus.fulfilled) {
          const prev = new Date(normalized)
          timestamp = new Date(prev.getTime() + POLL_INTERVAL).toISOString()
        } else {
          timestamp = normalized
        }
      } else {
        timestamp = new Date().toISOString()
      }

      lastDispatchTimeRef.current = now

      dispatch(
        pollLobbyQueueAsync({
          clinicId,
          timeStamp: timestamp,
        }) as any,
      )
    } catch (error) {
      pendingStartTimeRef.current = null
    }
  }, [clinicId, dispatch])

  // Initial load

  useEffect(() => {
    if (!clinicId) return

    if (!refreshDate || refreshDate !== today) {
      dispatch(loadLobbyQueueAsync({ clinicId }) as any)
    }
  }, [clinicId, refreshDate, today, dispatch])

  // Polling loop
  useEffect(() => {
    if (!clinicId) {
      return
    }

    // Start first poll
    executePoll()

    // Set up interval
    intervalRef.current = setInterval(() => {
      executePoll()
    }, POLL_INTERVAL)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      pendingStartTimeRef.current = null
    }
  }, [clinicId, executePoll])

  // Handle tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && clinicId) {
        // Force a poll when tab becomes visible
        pendingStartTimeRef.current = null
        executePoll()
      } else if (document.visibilityState === 'hidden') {
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [clinicId, executePoll])

  // Handle network reconnection
  useEffect(() => {
    const handleOnline = () => {
      pendingStartTimeRef.current = null
      executePoll()
    }

    const handleOffline = () => {
      // console.log('[LobbyPoll] Network disconnected')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [clinicId, executePoll])
}
